import type { FC } from "react";
import { memo } from "react";
import styled from "@emotion/styled";
import { Sparkles } from "lucide-react";
import {
  borders,
  colors,
  hideScrollbar,
  radius,
  shadows,
  spacing,
} from "../design-tokens";
import { ParagraphSmall } from "../primitives/typographies";
import type { FollowUpQuestion } from "@/types/chat-parts";
import { useIsMobile } from "@/hooks/use-mobile";

export type SuggestionItem = FollowUpQuestion & {
  displayText?: string; // Optional: show this text instead of the question
};

type SuggestionCardsProps = {
  suggestions: SuggestionItem[];
  onSuggestionClick: (query: string) => void;
  isColumnLayout?: boolean;
};

const FadeLeft = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: ${spacing.lg}px;
  background: linear-gradient(to right, ${colors.surface} 0%, transparent 100%);
  pointer-events: none;
`;

const FadeRight = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: ${spacing.lg}px;
  background: linear-gradient(to left, ${colors.surface} 0%, transparent 100%);
  pointer-events: none;
`;

const HorizontalScrollContainer = styled.div`
  position: relative;
  display: flex;
  overflow-x: auto;
  overflow-y: visible;
  scroll-snap-type: x mandatory;
  scroll-padding: ${spacing.md}px;
  gap: ${spacing.lg}px;
  padding: ${spacing.md}px;
  align-items: stretch;
  box-sizing: border-box;
  width: 100%;
  height: auto;
  ${hideScrollbar}

  /* Card sizing for horizontal scroll - fit parent container */
  & > * {
    --card-width: calc(100% - ${spacing.md * 2}px);
    flex: 0 0 var(--card-width);
    scroll-snap-align: center;
    max-width: var(--card-width);
  }
`;

const GridContainer = styled.div<{ isColumnLayout: boolean }>`
  display: grid;
  grid-template-columns: ${({ isColumnLayout }) =>
    isColumnLayout ? "1fr" : "repeat(3, 1fr)"};
  box-sizing: border-box;
  gap: ${spacing.lg}px;
  width: 100%;
  max-width: 1024px;
  margin: 0 auto;
  align-items: stretch;
  height: auto;
`;

// Card component
const StyledCard = styled.button`
  background-color: ${colors.background};
  ${borders.gradientBlue}
  border-radius: ${radius.md}px;
  padding: ${spacing.md}px;
  display: flex;
  gap: ${spacing.md}px;
  align-items: center;
  cursor: pointer;
  transition: all 200ms ease-in-out;
  box-sizing: border-box;
  ${shadows.accentShadow.default}
  height: auto;

  &:hover {
    ${shadows.accentShadow.hover}
  }

  &:active {
    ${shadows.accentShadow.active}
  }
`;

const SparkleIconMuted = styled.div`
  width: 32px;
  height: 32px;
  box-sizing: border-box;
  background-color: ${colors.surfaceSecondary};
  border-radius: ${radius.sm}px;
  padding: ${spacing.sm}px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.primary};
  flex-shrink: 0;
`;

const SparkleIcon = styled(Sparkles)`
  width: 100%;
  height: 100%;
  fill: ${colors.primary};
`;

const SuggestionText = styled(ParagraphSmall)`
  color: ${colors.text};
  font-weight: 500;
  line-height: 1.4;
  margin: 0;
  user-select: none;
  text-align: left;
`;

// Individual card component
const SuggestionCard: FC<{
  suggestion: SuggestionItem;
  onClick: () => void;
}> = memo(({ suggestion, onClick }) => {
  const displayText = suggestion.displayText ?? suggestion.question;

  return (
    <StyledCard
      onClick={onClick}
      type="button"
      aria-label={`Ask: ${displayText}`}
    >
      <SparkleIconMuted>
        <SparkleIcon />
      </SparkleIconMuted>
      <SuggestionText>{displayText}</SuggestionText>
    </StyledCard>
  );
});

export const SuggestionCards: FC<SuggestionCardsProps> = ({
  suggestions,
  onSuggestionClick,
  isColumnLayout = false,
}) => {
  const isMobile = useIsMobile();

  if (suggestions.length === 0) {
    return null;
  }

  const handleCardClick = (suggestion: SuggestionItem) => {
    onSuggestionClick(suggestion.question);
  };

  const cards = suggestions.map((suggestion, index) => (
    <SuggestionCard
      key={suggestion.question}
      suggestion={suggestion}
      onClick={() => handleCardClick(suggestion)}
    />
  ));

  if (isMobile) {
    return (
      <div style={{ position: "relative" }}>
        <HorizontalScrollContainer>{cards}</HorizontalScrollContainer>
        <FadeLeft />
        <FadeRight />
      </div>
    );
  }

  return <GridContainer isColumnLayout={isColumnLayout}>{cards}</GridContainer>;
};
